import { AppIcon } from '@/components/ui/app-icon';
import { AGENT_CARD_SHELL_SHADOW } from '@/components/agent-chat/agent-card.component';
import {
  CardCost,
  CardLinkButton,
} from '@/components/agent-chat/card-parts.component';
import { CLU_COLOURS } from '@/components/ui/clu-colours';
import { CluBird } from '@/components/ui/copilot-icon.component';
import { MARK_COLOURS_BY_KEY } from '@/containers/agents/agent-mark.util';
import { CONNECTORS } from '@/containers/onboarding/connector-logos';
import {
  logoUrl,
  SAMPLE_PEOPLE,
  type SamplePerson,
} from '@/containers/onboarding/first-run/sample-people';
import {
  iconArrowRight,
  iconCalendar,
  iconCheck,
  iconEnvelope,
  iconLock,
  iconPhone,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';
import { BRAND } from '@/lib/brand';

/**
 * Where to start, under Clu's first answer: four tiles in the home page's
 * quick-start look (a tinted preview of what it makes over the title), two
 * by two, then the setup call. In the live chat this is one more card
 * display after the answer, like the question card.
 */

export type FirstRunOptionId =
  | 'tour'
  | 'find'
  | 'sequence'
  | 'agent'
  // A Free workspace's two, in place of the sequence and the agent.
  | 'companies'
  | 'enrich';

export interface FirstRunOption {
  id: FirstRunOptionId;
  title: string;
  description: string;
}

const { gmail, linkedin, hubspot } = CONNECTORS;

const person = (name: string): SamplePerson =>
  SAMPLE_PEOPLE.find((p) => p.name === name) ?? SAMPLE_PEOPLE[0];

const LINE = 'border-black/[0.06] dark:border-white/[0.08]';

// ── Mini previews: a window rising from the tile's divider ─────────────────

function Window({ children }: { children: ReactNode }) {
  return (
    <div className='absolute inset-x-[24px] top-[16px] bottom-0 overflow-hidden rounded-t-[8px] bg-card shadow-[0_0_0_1px_hsl(var(--border)),0_-4px_16px_-8px_rgba(16,24,40,0.16)]'>
      {children}
    </div>
  );
}

function Face({ p, size = 14 }: { p: SamplePerson; size?: number }) {
  return (
    <img
      src={p.photo}
      alt=''
      className='shrink-0 rounded-full object-cover ring-1 ring-black/[0.06]'
      style={{ width: size, height: size }}
    />
  );
}

function Logo({ domain, size = 10 }: { domain: string; size?: number }) {
  return (
    <img
      src={logoUrl(domain)}
      alt=''
      className='shrink-0 rounded-[3px] object-cover'
      style={{ width: size, height: size }}
    />
  );
}

function Tick() {
  return (
    <span className='grid size-[10px] shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-600'>
      <AppIcon icon={iconCheck} style={{ fontSize: 5.5 }} />
    </span>
  );
}

/** Clu building a list in a small app window. */
function TourPreview() {
  const faces = [
    'Maya Chen',
    'Daniel Becker',
    'Amara Okafor',
    'Ravi Mehta',
  ].map(person);
  return (
    <Window>
      <div
        className={cn(
          'flex h-[16px] items-center gap-[3px] border-b px-[7px]',
          LINE
        )}
      >
        {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
          <span
            key={c}
            className='size-[5px] rounded-full'
            style={{ background: c }}
          />
        ))}
        <span className='mx-auto flex items-center gap-[3px] rounded-[4px] bg-muted px-[6px] text-[6.5px] leading-[10px] text-muted-foreground'>
          <AppIcon icon={iconLock} style={{ fontSize: 5 }} />
          {BRAND.appHost}
        </span>
      </div>
      <div className='space-y-[5px] px-[10px] py-[8px]'>
        <div className='flex items-center gap-[5px] text-[8.5px] font-semibold text-foreground'>
          <CluBird colours={CLU_COLOURS} className='size-[12px]' />
          Building your first list
          <span className='ml-auto flex items-center gap-[4px]'>
            <span className='flex -space-x-[4px]'>
              {faces.map((p) => (
                <span
                  key={p.name}
                  className='rounded-full ring-[1.5px] ring-card'
                >
                  <Face p={p} size={14} />
                </span>
              ))}
            </span>
            <span className='text-[7px] font-medium text-muted-foreground'>
              +108
            </span>
          </span>
        </div>
        <div className='flex items-center gap-[5px] text-[8px] text-foreground/75'>
          <Tick />
          48 companies like Ramp
        </div>
        <div className='flex items-center gap-[5px] text-[8px] text-foreground/75'>
          <Tick />
          112 RevOps leaders at them
        </div>
        <div className='flex items-center gap-[5px] text-[8px] text-foreground/75'>
          <span className='size-[10px] shrink-0 rounded-full border-[1.5px] border-primary/25 border-t-primary' />
          Finding work emails and phones
        </div>
      </div>
    </Window>
  );
}

/** People from the ICP: faces, titles, company logos. */
function PeoplePreview() {
  const rows = ['Maya Chen', 'Daniel Becker', 'Amara Okafor', 'Ravi Mehta'].map(
    person
  );
  const cols = 'grid grid-cols-[1.3fr_1.1fr_0.8fr] items-center gap-x-[8px]';
  return (
    <Window>
      <div
        className={cn(
          cols,
          'h-[18px] border-b bg-muted/50 px-[10px] text-[7px] font-medium text-muted-foreground',
          LINE
        )}
      >
        <span>Person</span>
        <span>Title</span>
        <span>Company</span>
      </div>
      {rows.map((p) => (
        <div
          key={p.name}
          className={cn(
            cols,
            'h-[19px] border-b px-[10px] text-[7.5px] text-foreground/85',
            LINE
          )}
        >
          <span className='flex min-w-0 items-center gap-[5px] font-medium'>
            <Face p={p} size={13} />
            <span className='truncate'>{p.name}</span>
          </span>
          <span className='truncate text-foreground/65'>{p.title}</span>
          <span className='flex min-w-0 items-center gap-[4px]'>
            <Logo domain={p.domain} />
            <span className='truncate'>{p.company}</span>
          </span>
        </div>
      ))}
    </Window>
  );
}

/** One prospect's steps across email, LinkedIn and a call. */
function SequencePreview() {
  const to = person('Samuel Adeyemi');
  const steps: { label: string; day: string; mark: ReactNode }[] = [
    {
      label: 'Email',
      day: 'Day 1',
      mark: <gmail.Mark className='size-[9px]' />,
    },
    {
      label: 'LinkedIn',
      day: 'Day 3',
      mark: <linkedin.Mark className='size-[9px]' />,
    },
    {
      label: 'Call',
      day: 'Day 6',
      mark: (
        <AppIcon
          icon={iconPhone}
          className='text-emerald-600'
          style={{ fontSize: 7 }}
        />
      ),
    },
  ];
  return (
    <Window>
      <div
        className={cn(
          'flex items-center gap-[6px] border-b px-[10px] py-[6px]',
          LINE
        )}
      >
        <Face p={to} size={16} />
        <span className='min-w-0'>
          <span className='block truncate text-[8px] leading-[10px] font-semibold text-foreground'>
            {to.name}
          </span>
          <span className='flex items-center gap-[3px] text-[6.5px] leading-[9px] text-muted-foreground'>
            <Logo domain={to.domain} size={7} />
            {to.title}, {to.company}
          </span>
        </span>
      </div>
      <div className='px-[10px] pt-[8px]'>
        <div className='flex items-center gap-[4px]'>
          {steps.map((s, i) => (
            <div key={s.label} className='flex flex-1 items-center gap-[4px]'>
              <div
                className={cn(
                  'flex flex-1 items-center gap-[4px] rounded-[5px] border px-[6px] py-[4px] text-[7.5px] font-medium text-foreground',
                  LINE
                )}
              >
                <span className='grid size-[9px] place-items-center'>
                  {s.mark}
                </span>
                {s.label}
                <span className='ml-auto font-normal text-muted-foreground'>
                  {s.day}
                </span>
              </div>
              {i < steps.length - 1 ? (
                <span className='h-px w-[6px] bg-border' />
              ) : null}
            </div>
          ))}
        </div>
        <p className='mt-[6px] truncate text-[7.5px] text-foreground/70'>
          <span className='font-medium text-foreground'>Subject:</span> Quick
          idea for the SDR team at Stripe
        </p>
      </div>
    </Window>
  );
}

/** An agent's flow: a schedule, two steps, HubSpot. */
function AgentPreview() {
  const nodes: { label: string; mark: ReactNode }[] = [
    {
      label: 'Every Monday',
      mark: (
        <AppIcon
          icon={iconCalendar}
          className='text-muted-foreground'
          style={{ fontSize: 7 }}
        />
      ),
    },
    {
      label: 'Find ICP people',
      mark: (
        <img
          src='/cleanlist-icon-black.svg'
          alt=''
          className='size-[8px] dark:invert'
        />
      ),
    },
    {
      label: 'Enrich',
      mark: (
        <AppIcon
          icon={iconCheck}
          className='text-emerald-600'
          style={{ fontSize: 7 }}
        />
      ),
    },
    { label: 'HubSpot', mark: <hubspot.Mark className='size-[9px]' /> },
  ];
  return (
    <Window>
      <div
        className={cn(
          'flex items-center gap-[5px] border-b px-[10px] py-[6px]',
          LINE
        )}
      >
        <CluBird colours={MARK_COLOURS_BY_KEY.violet} className='size-[13px]' />
        <span className='text-[8px] font-semibold text-foreground'>
          Weekly ICP leads
        </span>
        <span className='ml-auto flex items-center gap-[3px] text-[6.5px] font-medium text-emerald-700'>
          <span className='size-[4px] rounded-full bg-emerald-500' />
          Active
        </span>
      </div>
      <div className='flex h-full items-start bg-[radial-gradient(circle,hsl(var(--border))_0.6px,transparent_0.7px)] [background-size:8px_8px] px-[10px] pt-[14px]'>
        {nodes.map((n, i) => (
          <div key={n.label} className='flex items-center'>
            <div className='flex items-center gap-[4px] rounded-[5px] bg-card px-[6px] py-[4px] text-[7.5px] font-medium whitespace-nowrap text-foreground shadow-[0_0_0_1px_hsl(var(--border)),0_1px_2px_rgba(16,24,40,0.06)]'>
              <span className='grid size-[9px] place-items-center'>
                {n.mark}
              </span>
              {n.label}
            </div>
            {i < nodes.length - 1 ? (
              <span className='h-px w-[8px] bg-border' />
            ) : null}
          </div>
        ))}
      </div>
    </Window>
  );
}

/** Target accounts that fit the ICP: logos, names, sites. */
function CompaniesPreview() {
  const rows = ['Maya Chen', 'Daniel Becker', 'Amara Okafor', 'Ravi Mehta'].map(
    person
  );
  const cols = 'grid grid-cols-[1.4fr_1.2fr_0.5fr] items-center gap-x-[8px]';
  return (
    <Window>
      <div
        className={cn(
          cols,
          'h-[18px] border-b bg-muted/50 px-[10px] text-[7px] font-medium text-muted-foreground',
          LINE
        )}
      >
        <span>Company</span>
        <span>Website</span>
        <span>Fit</span>
      </div>
      {rows.map((p) => (
        <div
          key={p.company}
          className={cn(
            cols,
            'h-[19px] border-b px-[10px] text-[7.5px] text-foreground/85',
            LINE
          )}
        >
          <span className='flex min-w-0 items-center gap-[5px] font-medium'>
            <Logo domain={p.domain} size={12} />
            <span className='truncate'>{p.company}</span>
          </span>
          <span className='truncate text-foreground/65'>{p.domain}</span>
          <Tick />
        </div>
      ))}
    </Window>
  );
}

/** One person's email and mobile, found. */
function EnrichPreview() {
  const p = person('Claire Dubois');
  const found: { icon: typeof iconEnvelope; label: string; value: string }[] = [
    { icon: iconEnvelope, label: 'Work email', value: `c••••@${p.domain}` },
    { icon: iconPhone, label: 'Mobile', value: '+1 (415) •••-••18' },
  ];
  return (
    <Window>
      <div
        className={cn(
          'flex items-center gap-[6px] border-b px-[10px] py-[7px]',
          LINE
        )}
      >
        <Face p={p} size={18} />
        <span className='min-w-0'>
          <span className='block truncate text-[8.5px] leading-[11px] font-semibold text-foreground'>
            {p.name}
          </span>
          <span className='flex items-center gap-[3px] truncate text-[7px] leading-[10px] text-muted-foreground'>
            <Logo domain={p.domain} size={8} />
            {p.title}, {p.company}
          </span>
        </span>
      </div>
      {found.map((row) => (
        <div
          key={row.label}
          className={cn(
            'flex h-[20px] items-center gap-[6px] border-b px-[10px] text-[7.5px]',
            LINE
          )}
        >
          <AppIcon
            icon={row.icon}
            className='text-muted-foreground'
            style={{ fontSize: 7 }}
          />
          <span className='w-[44px] text-muted-foreground'>{row.label}</span>
          <span className='min-w-0 flex-1 truncate font-medium text-foreground/85'>
            {row.value}
          </span>
          <Tick />
        </div>
      ))}
    </Window>
  );
}

const PREVIEWS: Record<FirstRunOptionId, () => ReactNode> = {
  tour: TourPreview,
  find: PeoplePreview,
  sequence: SequencePreview,
  agent: AgentPreview,
  companies: CompaniesPreview,
  enrich: EnrichPreview,
};

/**
 * Each tile's tint, from the home quick-start palette (the sanctioned
 * accent-only literals) and the brand colour for the tour.
 */
const TINTS: Record<FirstRunOptionId, string> = {
  tour: 'from-primary/[0.13]',
  find: 'from-sky-500/[0.13]',
  sequence: 'from-emerald-500/[0.13]',
  agent: 'from-violet-500/[0.13]',
  companies: 'from-amber-500/[0.13]',
  enrich: 'from-violet-500/[0.13]',
};

function OptionTile({
  option,
  onSelect,
}: {
  option: FirstRunOption;
  onSelect: (id: FirstRunOptionId) => void;
}) {
  const Preview = PREVIEWS[option.id];
  return (
    <button
      type='button'
      onClick={() => onSelect(option.id)}
      className={cn(
        'group flex min-w-0 flex-col overflow-hidden rounded-[8px] bg-card text-left transition-shadow duration-200',
        AGENT_CARD_SHELL_SHADOW,
        'hover:shadow-[0_0_0_1px_hsl(var(--border)),0_8px_20px_-8px_rgba(16,24,40,0.16)]',
        'focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-hidden'
      )}
    >
      <div
        aria-hidden='true'
        className={cn(
          'relative h-[104px] overflow-hidden bg-linear-to-b to-transparent',
          TINTS[option.id]
        )}
      >
        <div className='absolute inset-0 transition-transform duration-300 group-hover:-translate-y-[2px]'>
          <Preview />
        </div>
      </div>
      <div className='flex items-start gap-[10px] border-t border-border px-[14px] py-[12px]'>
        <div className='min-w-0 flex-1'>
          <p className='text-[14px] leading-[20px] font-medium text-foreground'>
            {option.title}
          </p>
          <p className='mt-[2px] line-clamp-2 text-[12px] leading-[16px] text-muted-foreground'>
            {option.description}
          </p>
        </div>
        <span className='mt-[2px] grid size-[24px] shrink-0 place-items-center rounded-full bg-muted text-foreground/70 transition-colors group-hover:bg-foreground group-hover:text-background'>
          <AppIcon icon={iconArrowRight} style={{ fontSize: 10 }} />
        </span>
      </div>
    </button>
  );
}

export function FirstRunOptions({
  options,
  onSelect,
  call,
}: {
  options: FirstRunOption[];
  onSelect: (id: FirstRunOptionId) => void;
  /** The onboarding call offer, when the org can still claim it. */
  call?: {
    host: string;
    photo?: string;
    credits: number;
    onBook: () => void;
  } | null;
}) {
  return (
    <div className='flex flex-col gap-[8px]'>
      <div className='grid grid-cols-2 gap-[8px]'>
        {options.map((option) => (
          <OptionTile key={option.id} option={option} onSelect={onSelect} />
        ))}
      </div>
      {call ? (
        <div className='flex items-center gap-[10px] rounded-[8px] border border-dashed border-border px-[12px] py-[10px]'>
          {call.photo ? (
            <img
              src={call.photo}
              alt=''
              className='size-[32px] shrink-0 rounded-full object-cover ring-1 ring-black/[0.06]'
            />
          ) : null}
          <p className='min-w-0 flex-1 truncate text-[14px] leading-[20px] font-medium text-foreground'>
            30-minute setup call with {call.host}
          </p>
          <CardCost amount={call.credits} plus />
          <CardLinkButton onClick={call.onBook}>Book</CardLinkButton>
        </div>
      ) : null}
    </div>
  );
}
